import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, WifiOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  desconectarMeta,
  desconectarWhatsapp,
  fetchCadastroMeta,
  fetchConfiguracao,
  type Configuracao,
} from '@/api/secretario';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { maskTelefone } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { ConectarPelaMeta } from './canal/ConectarPelaMeta';
import { ConectarPorQr } from './canal/ConectarPorQr';
import { NumerosIgnorados } from './NumerosIgnorados';
import { WizardActions } from './WizardActions';

interface StepProps {
  config: Configuracao | null;
  onNext: () => void;
  onBack: () => void;
  isFirst: boolean;
}

function formatarNumero(e164: string): string {
  return e164.startsWith('+55') ? `+55 ${maskTelefone(e164.slice(3))}` : e164;
}

/**
 * Passo 4 — o número de WhatsApp do Secretário. O caminho oficial é a Meta
 * (cadastro embutido); o QR code (Evolution) fica como alternativa
 * (docs/PLANO_SECRETARIO_WHATSAPP_META.md e PLANO_SECRETARIO_WHATSAPP_EVOLUTION.md).
 */
export function CanalStep({ config, onNext, onBack, isFirst }: StepProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [qrAberto, setQrAberto] = useState(false);

  const { data: atual } = useQuery({ queryKey: queryKeys.secretario.config, queryFn: fetchConfiguracao });
  const { data: cadastroMeta } = useQuery({
    queryKey: ['secretario', 'cadastro-meta'],
    queryFn: fetchCadastroMeta,
    enabled: config != null,
    staleTime: Infinity,
  });
  const canal = (atual ?? config)?.canal ?? null;
  const conectado = canal?.status === 'CONECTADO';
  const desconectado = canal?.status === 'DESCONECTADO';
  const metaDisponivel = cadastroMeta?.disponivel === true;

  const desconectar = useMutation({
    mutationFn: () => (canal?.provider === 'META' ? desconectarMeta() : desconectarWhatsapp()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess('WhatsApp desconectado.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  const podeDesconectar = canal?.provider === 'EVOLUTION' || canal?.provider === 'META';

  // Leu o QR: o canal chegou conectado. Anunciado aqui, porque o bloco do QR some
  // no mesmo instante em que o passo passa a mostrar o número conectado.
  const qrPendente = useRef(false);
  useEffect(() => {
    if (conectado && qrPendente.current) {
      qrPendente.current = false;
      showSuccess('WhatsApp conectado.');
    }
  }, [conectado, showSuccess]);
  const aoMudarQr = (aguardando: boolean) => {
    qrPendente.current = aguardando;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Número de WhatsApp</CardTitle>
        <CardDescription>Por onde os clientes vão falar com o seu Secretário.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {conectado && canal ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
            <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Seu número de atendimento</p>
              <p className="text-lg font-semibold tracking-tight">{formatarNumero(canal.numero_e164)}</p>
              {canal.provider === 'META' && (
                <p className="text-xs text-muted-foreground">Conexão oficial pela Meta.</p>
              )}
            </div>
            {podeDesconectar && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => desconectar.mutate()}
                disabled={desconectar.isPending}
              >
                {desconectar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
                Desconectar
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {desconectado && (
              <Alert variant="destructive">
                <WifiOff className="h-4 w-4" />
                <AlertTitle>Seu WhatsApp foi desconectado</AlertTitle>
                <AlertDescription>
                  O Secretário parou de atender pelo {canal ? formatarNumero(canal.numero_e164) : 'seu número'}. Conecte
                  o número de novo para voltar a atender.
                </AlertDescription>
              </Alert>
            )}
            {metaDisponivel && cadastroMeta ? (
              <>
                <ConectarPelaMeta cadastro={cadastroMeta} />
                <div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="px-0 text-muted-foreground"
                    aria-expanded={qrAberto}
                    onClick={() => setQrAberto((aberto) => !aberto)}
                  >
                    <ChevronDown className={`mr-1 h-4 w-4 transition-transform ${qrAberto ? 'rotate-180' : ''}`} />
                    Outra forma: conectar por QR code
                  </Button>
                  {qrAberto && (
                    <div className="pt-2">
                      <ConectarPorQr conectado={conectado} reconectar={desconectado} onAguardandoChange={aoMudarQr} />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <ConectarPorQr conectado={conectado} reconectar={desconectado} onAguardandoChange={aoMudarQr} />
            )}
          </div>
        )}

        {config && <NumerosIgnorados />}

        <WizardActions
          isFirst={isFirst}
          onBack={onBack}
          primaryLabel="Continuar"
          onPrimary={onNext}
          primaryDisabled={!conectado}
        />
      </CardContent>
    </Card>
  );
}
